import { AppBar, Box, Button, Grid, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedSnackbar,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import {
	CLIENT_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {  formatDate, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { CountryService } from "../../services/common/CountryService";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBMaximumEarnings } from "../../models/WSIBMaximumEarnings";
import { PrimeDataGrid } from "../PrimeDataGrid";
import { WSIBMaximumEarningsListHeader } from "./WSIBMaximumEarningsListHeader";
import { WSIBMaximumEarningsListDataGridTable } from "./WSIBMaximumEarningsListDataGridTable/WSIBCategoryListDataGridTable";
import { WSIBMaximumEarningsService } from "../../services/WSIBMaximumEarningsService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";

export interface IWSIBMaximumEarningsListHeader { }
const WSIB_Max_EARNINGS_LIST_QUERY_KEY = "wsib-max-earnings-list";

const useWSIBMaximumEarnings = (
	runGetListAPICall: boolean,

	provinceId: string,
	effectiveDate: String | null,
	endDate: string | null,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [

			WSIB_Max_EARNINGS_LIST_QUERY_KEY,
			runGetListAPICall,
			provinceId,
			effectiveDate,
			endDate,
			params,
			navigateToLogout
		],
		queryFn: (data) => {

			return WSIBMaximumEarningsService.getWSIBMaximumEarnings(
				runGetListAPICall,
				provinceId,
				effectiveDate,
				endDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: (data) => {
			return CountryService.getCountryDDL();
		},
		refetchOnWindowFocus: false,
	});

export const WSIBMaximumEarningsList: React.FC<IWSIBMaximumEarningsListHeader> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoadingProvinceDDL, setIsLoadingProvinceDDL] = useState(false);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "province",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { id } = useSelector((state: IAppState) => state.accountant);

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroWSIBMaximumEarnings, setzeroWSIBMaximumEarnings] = useState(true);
	const [wsibMaximumEarningsListUpdatedItems, setWSIBMaximumEarningsListUpdatedItems] =
		useState<any>(undefined);

	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [wsibMaximumEarningsId, setWSIBMaximumEarningsId] = useState<any>(0);

	// Bulk Delete
	const [bulkDelete, setBulkDelete] = useState<boolean>(false);
	const [bulkWSIBMaximumEarningsId, setBulkWSIBMaximumEarningsId] = useState<any>({});
	const [bulkWSIBMaximumEarningsDelete, setBulkWSIBMaximumEarningsDelete] =
		useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "province",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isPending: isPendingCountryDDL, data: countryDDL } = useGetCountryDDL();

	const provinceId = formMethods.getValues("provinceId");
	const effectiveDate = formMethods.getValues("effectiveDate");
	const endDate = formMethods.getValues("endDate");

	const { isPending, data } = useWSIBMaximumEarnings(
		runGetListAPICall,
		provinceId,
		formatDate(effectiveDate),
		formatDate(endDate),
		queryParams,
		navigateToLogout
	);
	const wsibMaximumEarningsList: any | null = data;


	useEffect(() => {
		if (wsibMaximumEarningsList) {
			setWSIBMaximumEarningsListUpdatedItems(wsibMaximumEarningsList)
		}
	}, [wsibMaximumEarningsList])

	// on apply button click api call wsib maximum-earnings list 
	const wsibMaximumEarningsListDataAPICall = (

		provinceId: string,

		effectiveDate: String,
		endDate: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await WSIBMaximumEarningsService.getWSIBMaximumEarnings(
					true,


					provinceId,

					formatDate(effectiveDate),
					formatDate(endDate),
					queryParams,
					navigateToLogout
				);

				if (response) {
					setWSIBMaximumEarningsListUpdatedItems(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
	
		wsibMaximumEarningsListDataAPICall(


			values.provinceId,

			values.effectiveDate,
			values.endDate,
		);
	};


	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroWSIBMaximumEarnings(false);
	};

	const onRowEditClicked = useCallback((params: IWSIBMaximumEarnings) => {
		let path = `/workplace-safety/maximum-earnings/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	// For Delete Row
	const deleteWSIBMaximumEarningsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibMaximumEarningsId: wsibMaximumEarningsId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBMaximumEarningsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: IWSIBMaximumEarnings) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setApiAction("delete");
		setWSIBMaximumEarningsId(params.id);
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBMaximumEarningsById } = useMutation({
		mutationFn: WSIBMaximumEarningsService.delWSIBMaximumEarnings,

		onSuccess: (res: string | IServerError | IResponse | any) => {

			if (typeof (res) === 'string') {
				// remove the cache of the Year fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: res,
					severity: "success",
				});

				return queryClient.invalidateQueries({
					queryKey: [WSIB_Max_EARNINGS_LIST_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : "success",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setStatusValue(event.target.value as string);
		setApiAction("updateStatus");
		setDialogOpen(true);
	};

	// const onKeyFieldClickHandler = (clickedRow: any) => {
	// dispatch(setSelectedWSIBMaximumEarnings(clickedRow));
	// dispatch(setSelectedWSIBMaximumEarningsName(clickedRow));
	// };

	//bulk delete

	const { isPending: isBulkDeleting, mutate: deleteBulkWSIBMaximumEarningses } =
		useMutation({
			mutationFn: WSIBMaximumEarningsService.delBulkWSIBMaximumEarningses,
			onSuccess: (res: any | string | IServerError | IResponse) => {
				if (typeof res === "object") {
					// remove the cache of the Year fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong.",
						severity: res.status === false ? "error" : "success",
					});

					setBulkWSIBMaximumEarningsDelete(false);
					return queryClient.invalidateQueries({
						queryKey: [WSIB_Max_EARNINGS_LIST_QUERY_KEY],
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				setBulkWSIBMaximumEarningsDelete(false);
			},
			onError: (err: any) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
				setBulkWSIBMaximumEarningsDelete(false);
			},
		});

	// For Bulk Delete
	const deleteBulkWSIBMaximumEarningsField = () => {
		if (bulkDelete) {
			setBulkDelete(false);
			let payload = {
				wsibMaximumEarningsIds: bulkWSIBMaximumEarningsId,
				navigateToLogout: navigateToLogout,
			};
			deleteBulkWSIBMaximumEarningses(payload);
			setDialogOpen(false);
		}
	};

	const onRowMultipleDeleteClicked = (params: any) => {

		let bulkDeletePayload = {
			wsibMaximumEarningsIds: { wsibMaximumEarningsIds: params },
		};
		setDialogOpen(true);
		setBulkDelete(true);
		setApiAction("delete");
		setBulkWSIBMaximumEarningsId(bulkDeletePayload);
		setBulkWSIBMaximumEarningsDelete(true);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		// if (selectedItems.length) {
		// 	return (
		// 		<Box sx={{ marginTop: theme.spacing(1) }}>
		// 			<Button
		// 				onClick={() => onRowMultipleDeleteClicked(selectedItems)}
		// 				variant="contained"
		// 				color="error"
		// 			>
		// 				Delete
		// 			</Button>
		// 		</Box>
		// 	);
		// }
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton
			// 		to="/company/wsibMaximumEarnings/setup"
			// 		text="Add WSIBMaximumEarnings"
			// 	/>
			// </Box>
			<></>
		);
	}, []);

	const upadtedWSIBMaximumEarningsListItems = useMemo(() => {
		return (
			wsibMaximumEarningsListUpdatedItems &&
			wsibMaximumEarningsListUpdatedItems.items &&
			wsibMaximumEarningsListUpdatedItems?.items.map((item: any) => ({
				...item,

			}))
		);
	}, [wsibMaximumEarningsListUpdatedItems]);

	

	const expandedLabels = ["Workplace Safety", "Maximum Earnings"];
	const expandedWSIBMaximumEarningsListOrSetupItem = "List"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Maximum Earnings" &&
				item.label === expandedWSIBMaximumEarningsListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Maximum Earnings" && item.label === expandedWSIBMaximumEarningsListOrSetupItem && "/workplace-safety/maximum-earnings/list",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: CLIENT_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_wsibMaximumEarnings_list.png";

	return (
		// wsibMaximumEarningsList &&
		// 	wsibMaximumEarningsList.items &&
		// 	wsibMaximumEarningsList.items.length === 0 &&
		// 	zeroWSIBMaximumEarnings === true ? (
		// 	<EmptyList
		// 		showImage={true}
		// 		imagePath={image}
		// 		heading={`There's nothing! Add WSIBMaximumEarnings to proceed`}
		// 		content={`You can add WSIBMaximumEarnings by directly clicking on add WSIBMaximumEarnings button.`}
		// 		testId={"wsibMaximumEarnings-empty-list"}
		// 		showAddButton={true}
		// 		addButtonLabel={"Add WSIBMaximumEarnings"}
		// 		navigateToAdd={"/company/wsibMaximumEarnings/setup"}
		// 		showImportButton={false}
		// 		showImportXmlButton={false}
		// 	/>
		// ) : (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Maximum Earnings List"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<WSIBMaximumEarningsListHeader
						countryDDL={countryDDL && countryDDL}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						setIsLoadingProvinceDDL={(value: boolean) => setIsLoadingProvinceDDL(value)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending || isLoading || isLoadingProvinceDDL || isPendingCountryDDL
					}
					testId="wsib-maximum-earnings-list"
				/>

				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={
						bulkWSIBMaximumEarningsDelete === true
							? deleteBulkWSIBMaximumEarningsField
							: deleteWSIBMaximumEarningsField
					}
					cancelActionHandler={onCloseHandler}
					testId="wsib-maximum-earnings-list-confirmation-dialog"
				/>
				<WSIBMaximumEarningsListDataGridTable
					columns={wsibMaximumEarningsListUpdatedItems ? wsibMaximumEarningsListUpdatedItems.headerColumns : wsibMaximumEarningsList?.headerColumns ?? []}
					rows={
						wsibMaximumEarningsListUpdatedItems && upadtedWSIBMaximumEarningsListItems
							? upadtedWSIBMaximumEarningsListItems
							: wsibMaximumEarningsList?.items ?? []
						// wsibMaximumEarningsList?.items ?? []
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={true}
					onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					isLoading={isPending || isDeleting || isBulkDeleting}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						wsibMaximumEarningsListUpdatedItems && wsibMaximumEarningsListUpdatedItems.total ? wsibMaximumEarningsListUpdatedItems.total : 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					// searchFieldPlaceholder={"Search by Name..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroWSIBMaximumEarnings(value)
					}
				// pageTitle="WSIB Maximum Earnings List"
				/>


				{wsibMaximumEarningsList ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}

			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
